/**
 * Supervisão pendente — a ÚNICA cópia local do blob, durável e com dono.
 *
 * Antes existiam dois problemas casados: (a) a supervisão não tinha fila, era um
 * POST direto com debounce cujo erro era engolido, então preenchimento offline
 * nunca subia; e (b) a cópia local em `caint-mobile-v2` NUNCA era apagada após o
 * envio, mascarando o servidor para sempre. Resolver (a) sem (b) seria pior:
 * dar retry automático a um blob velho o faz sobrescrever com mais confiabilidade.
 *
 * O desenho evita as duas coisas guardando UMA cópia com metadados, em vez de
 * uma fila separada + um cache que precisam concordar entre si:
 *
 * - "não confirmado" é `rev > revEnviada` — não existe delete, nem corrida de delete;
 * - a fila É o conjunto sujo, então não há segunda quarentena a reconciliar;
 * - sobreviver ao logout é estrutural: a chave não faz parte do estado do app;
 * - `versaoBase` carrega a versão do servidor sobre a qual se editou, para o
 *   servidor recusar escrita obsoleta (doc 20 §I6).
 *
 * Classe pura sobre `KeyValueStore` — testável com `MemoryStore`, sem React.
 */
import type { SupervisaoData } from "@caint/shared";
import type { KeyValueStore } from "../persistence/port.ts";

const CHAVE = "caint-supervisao-pendente-v1";

/**
 * Situação de uma entrada:
 * - `ativa`: fluxo normal; drena quando houver rede e o dono estiver logado.
 * - `aguardando-sessao`: 401 — token morto. Re-tentar não adianta; drena no
 *   próximo login bem-sucedido.
 * - `quarentena`: 4xx determinístico (403/404). Não drena sozinha e NUNCA é
 *   apagada em silêncio — supervisão é trabalho de campo, não log.
 * - `legado`: veio do formato antigo, de idade desconhecida. Não sobe sozinha:
 *   exige um toque do usuário, senão um cache velho sobrescreveria dado bom.
 */
export type EstadoPendente = "ativa" | "aguardando-sessao" | "quarentena" | "legado";

export interface EntradaPendente {
  readonly avaliacaoId: string;
  readonly dados: SupervisaoData;
  /** Contador local de edições. */
  readonly rev: number;
  /** Valor de `rev` no último envio confirmado. `rev > revEnviada` ⇒ sujo. */
  readonly revEnviada: number;
  /** Versão do servidor sobre a qual se editou (concorrência otimista). */
  readonly versaoBase: number | null;
  /** Dono — só ele pode enviar, senão a autoria gravada sairia errada. */
  readonly usuarioId: string;
  /** Nome do dono, para a mensagem dizer de QUEM é a pendência. */
  readonly usuarioNome: string;
  readonly estado: EstadoPendente;
  readonly atualizadoEm: string;
}

type Mapa = Record<string, EntradaPendente>;

/**
 * Qual cópia a TELA deve mostrar: a local ou a do servidor.
 *
 * Não basta perguntar "está suja?". Ao confirmar o envio a entrada fica limpa
 * NA HORA, mas o `pull` que traz a gravação de volta vem depois — e o `sync()`
 * periódico drena sem puxar nenhuma vez. Nessa janela, decidir pela sujeira fazia
 * o campo voltar ao texto ANTIGO do servidor: o usuário via o que acabou de
 * digitar sumir, às vezes voltando (quando havia pull logo em seguida), às vezes
 * não (quando o flush veio do `sync`). Relato de 24/ago/2026.
 *
 * A pergunta certa é se o SERVIDOR JÁ TEM o que gravamos, e a versão responde
 * isso: `versaoBase` guarda a versão que o servidor atribuiu à NOSSA escrita.
 * Enquanto o pull não alcançar esse número, a verdade local é a mais nova.
 */
export function blobVigente(
  entrada: EntradaPendente | undefined,
  versaoServidor: number,
): "local" | "servidor" {
  if (!entrada) {
    return "servidor";
  }
  if (suja(entrada)) {
    return "local"; // trabalho ainda não enviado
  }
  if ((entrada.versaoBase ?? 0) > versaoServidor) {
    return "local"; // enviado, mas o pull ainda não trouxe de volta
  }
  return "servidor"; // o servidor alcançou — ele é a fonte
}

/** Uma entrada está suja (tem trabalho não confirmado)? */
export function suja(e: EntradaPendente): boolean {
  return e.rev > e.revEnviada;
}

/** Resultado de um flush, no mesmo formato do `SyncOutbox`. */
export interface ResultadoFlushSup {
  readonly confirmadas: number;
  /** Recusadas por obsolescência — o servidor tem algo mais novo. */
  readonly obsoletas: number;
  /** Entregues, mas com a transição recusada (409 — avaliação já encaminhada). */
  readonly entreguesSemValidar: number;
  readonly quarentena: number;
  /** Aguardando novo login (401). */
  readonly aguardandoSessao: number;
  /** Ainda sujas após o flush. */
  readonly pendentes: number;
}

/** O que a fila precisa da API — porta estreita, fácil de fingir no teste (ISP). */
export type EnviarSupervisao = (entrada: EntradaPendente) => Promise<{ aplicado: boolean; versao: number }>;

export class SupervisaoPendente {
  constructor(
    private readonly store: KeyValueStore,
    private readonly agora: () => string = () => new Date().toISOString(),
  ) {}

  private mapa(): Mapa {
    return this.store.read<Mapa>(CHAVE, {});
  }

  private gravar(m: Mapa): boolean {
    return this.store.write(CHAVE, m);
  }

  todas(): EntradaPendente[] {
    return Object.values(this.mapa());
  }

  porAvaliacao(avaliacaoId: string): EntradaPendente | null {
    return this.mapa()[avaliacaoId] ?? null;
  }

  /**
   * Registra uma edição. Incrementa `rev` — é isso que marca "sujo" e o que
   * protege a edição feita DURANTE um envio em voo: o remetente fotografa `rev`
   * ao serializar e, no ack, só marca como enviado até aquela foto.
   *
   * Devolve `false` se a gravação falhou (cota cheia) — o chamador PRECISA
   * avisar, senão volta a ser perda silenciosa.
   */
  editar(
    avaliacaoId: string,
    dados: SupervisaoData,
    dono: { id: string; nome: string },
    versaoBase: number | null,
  ): boolean {
    const m = this.mapa();
    const atual = m[avaliacaoId];
    m[avaliacaoId] = {
      avaliacaoId,
      dados,
      rev: (atual?.rev ?? 0) + 1,
      revEnviada: atual?.revEnviada ?? 0,
      // A versão do servidor é sempre a mais recente conhecida.
      versaoBase: versaoBase ?? atual?.versaoBase ?? null,
      usuarioId: dono.id,
      usuarioNome: dono.nome,
      // Editar reativa o que estava esperando sessão; quarentena/legado exigem ação.
      estado: atual?.estado === "quarentena" || atual?.estado === "legado" ? atual.estado : "ativa",
      atualizadoEm: this.agora(),
    };
    return this.gravar(m);
  }

  /** Marca o estado de uma entrada (sessão expirada, quarentena, reativação). */
  marcar(avaliacaoId: string, estado: EstadoPendente): void {
    const m = this.mapa();
    const e = m[avaliacaoId];
    if (!e) {
      return;
    }
    m[avaliacaoId] = { ...e, estado };
    this.gravar(m);
  }

  /** Descarta a entrada — só para casos em que o servidor já tem a verdade. */
  descartar(avaliacaoId: string): void {
    const m = this.mapa();
    delete m[avaliacaoId];
    this.gravar(m);
  }

  /**
   * Confirma o envio até a revisão fotografada. Se `rev` avançou durante o voo,
   * a entrada CONTINUA suja e o próximo flush a leva — nenhuma tecla se perde.
   */
  confirmar(avaliacaoId: string, revEnviada: number, versao: number): void {
    const m = this.mapa();
    const e = m[avaliacaoId];
    if (!e) {
      return;
    }
    m[avaliacaoId] = { ...e, revEnviada, versaoBase: versao, estado: "ativa" };
    this.gravar(m);
  }

  /** Entradas sujas que o usuário logado pode enviar agora. */
  prontasPara(usuarioId: string): EntradaPendente[] {
    return this.todas().filter((e) => e.estado === "ativa" && suja(e) && e.usuarioId === usuarioId);
  }

  /**
   * Quantas pendências contam para o indicador. Só as do usuário logado: um
   * badge permanentemente não-zero por causa de pendência alheia ensina todo
   * mundo a ignorá-lo.
   */
  totalPendente(usuarioId: string): number {
    return this.todas().filter((e) => suja(e) && e.usuarioId === usuarioId && e.estado !== "quarentena")
      .length;
  }

  /** Pendências de OUTRA pessoa neste aparelho — visíveis, nunca apagadas. */
  deOutros(usuarioId: string): EntradaPendente[] {
    return this.todas().filter((e) => suja(e) && e.usuarioId !== usuarioId);
  }

  /**
   * Envia uma a uma, em série. A serialização importa: dois envios simultâneos
   * do mesmo blob podem chegar fora de ordem, e o servidor ordena por chegada.
   */
  async flush(usuarioId: string, enviar: EnviarSupervisao): Promise<ResultadoFlushSup> {
    let confirmadas = 0;
    let obsoletas = 0;
    let entreguesSemValidar = 0;
    let quarentena = 0;
    let aguardandoSessao = 0;

    for (const entrada of this.prontasPara(usuarioId)) {
      const revNoEnvio = entrada.rev; // foto ANTES do IO
      try {
        const r = await enviar(entrada);
        if (r.aplicado) {
          this.confirmar(entrada.avaliacaoId, revNoEnvio, r.versao);
          confirmadas++;
        } else {
          // Obsoleta: outro dispositivo gravou depois. Re-tentar só repete a
          // derrota — o certo é soltar e deixar o pull trazer a verdade nova.
          this.descartar(entrada.avaliacaoId);
          obsoletas++;
        }
      } catch (e) {
        const classe = classificar(e);
        if (classe === "transitorio") {
          break; // rede/5xx: para o lote, tenta tudo de novo depois
        }
        if (classe === "sessao") {
          this.marcar(entrada.avaliacaoId, "aguardando-sessao");
          aguardandoSessao++;
        } else if (classe === "entregue") {
          // 409: o blob FOI gravado, só a transição foi recusada. Re-tentar
          // repetiria para sempre uma escrita bem-sucedida.
          this.confirmar(entrada.avaliacaoId, revNoEnvio, entrada.versaoBase ?? 0);
          entreguesSemValidar++;
        } else {
          this.marcar(entrada.avaliacaoId, "quarentena");
          quarentena++;
        }
      }
    }
    return {
      confirmadas,
      obsoletas,
      entreguesSemValidar,
      quarentena,
      aguardandoSessao,
      pendentes: this.totalPendente(usuarioId),
    };
  }

  /** Reativa o que ficou esperando sessão — chamar após login bem-sucedido. */
  reativarPorSessao(usuarioId: string): void {
    for (const e of this.todas()) {
      if (e.estado === "aguardando-sessao" && e.usuarioId === usuarioId) {
        this.marcar(e.avaliacaoId, "ativa");
      }
    }
  }
}

/** Como tratar cada falha do `POST /supervisao`. */
export type ClasseErro = "transitorio" | "sessao" | "entregue" | "veneno";

/**
 * Classificação deliberadamente diferente do `SyncOutbox`, porque o endpoint é
 * outro:
 * - 401 não é veneno, é sessão expirada — o `SyncOutbox` envenena e isso é um
 *   defeito dele, tratado à parte;
 * - 409 significa ENTREGUE (o backend grava antes de recusar a transição), então
 *   re-tentar seria repetir uma escrita que deu certo.
 */
export function classificar(e: unknown): ClasseErro {
  const status = (e as { status?: number })?.status;
  if (typeof status !== "number" || status >= 500) {
    return "transitorio";
  }
  if (status === 401) {
    return "sessao";
  }
  if (status === 409) {
    return "entregue";
  }
  return "veneno";
}
